import _ from "lodash";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import cx from "classnames";
import PropTypes from "prop-types";

// HOCs
import withRecorder from "../WithRecorder";
import I18nHOC from "UIComponents/I18nHOC";
import MediaPlaceholder from "UIComponents/MediaPlaceholder";

// apollo client methods
import { compose } from "lib/apolloClient";

// redux methods
import { connect } from "react-redux";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  downloadFromUrl,
  updateAudioRecorderBlob,
  setAudioRecorderActive,
} from "modules/Services";
import { Alert, Button, Dialog, IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  CloseOutlined,
  SquareFilled,
  PauseFilled,
  ScreenRecordOutlined,
  DotsSixVerticalOutlined,
  MicrophoneOutlined,
  ReloadArrowOutlined,
  TickCircleFilled,
} from "@toddle-edu/ds-icons";

// styles
import classes from "./AudioRecorderV5.scss";
import { prepareAttachmentFromBlob } from "Utils";
import { createPortal } from "react-dom";

const SAFE_SPACE = 16;
const DRAG_STEP_SIZE = 10;
const DRAG_STEP_SIZE_WITH_SHIFT = 50;

const calculateClampedPosition = (
  recorderHeightWithSafeSpace,
  recorderWidthWithSafeSpace,
  x,
  y
) => {
  const maxX = window.innerWidth - recorderWidthWithSafeSpace;
  const maxY = window.innerHeight - recorderHeightWithSafeSpace;

  // Clamp the new position within the screen bounds
  const clampedX = Math.max(recorderWidthWithSafeSpace, Math.min(x, maxX));
  const clampedY = Math.max(recorderHeightWithSafeSpace, Math.min(y, maxY));

  return { clampedX, clampedY };
};
const AudioRecorderV5 = forwardRef((props, forwardedRef) => {
  const {
    t,
    draftRecordingBlob,
    formattedLimit,
    isPaused,
    isPermissionGranted,
    hadDraftRecording,
    handleVisualiserRefUpdate,
    pauseStateBlob,
    showLimit = true,
    timer,
    cancelRecording,
    closeModal,
    restartRecording,
    startRecording,
    stopRecording,
    togglePause,
    submitText,
    activeAudioRecorder,
    setAudioRecorderActive,
    position: initialPosition = {},
    testId = "audioRecorder",
    showAudioDownloadButton,
    shouldBeResponsive,
  } = props;

  // hooks
  const [alertVisiblity, setAlertVisibility] = useState(false);
  const [isPreRecordingState, setIsPreRecordingState] = useState(false);
  const [preRecordingTimer, setPreRecordingTimer] = useState({
    timeoutId: null,
    time: 3,
    intervalId: null,
    showTimer: true,
  });
  const [isRecording, setIsRecording] = useState(false);
  const [isRecordingComplete, setIsRecordingComplete] = useState(false);
  const [isStartOverDialogOpen, setIsStartOverDialogOpen] = useState(false);
  const [draftAlertVisibility, setDraftAlertVisibility] = useState(
    hadDraftRecording
  );
  const audioRecorderRef = useRef();
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [insideResumebutton, setInsideResumebutton] = useState(false);
  const [isKeyboardDragActive, setIsKeyboardDragActive] = useState(false);

  const startRecordingButtonRef = useRef(null);
  const pauseResumeButtonRef = useRef(null);
  const doneButtonRef = useRef(null);
  const dragHandleRef = useRef(null);
  const liveRegionRef = useRef(null);
  const keyboardDragStartPositionRef = useRef(null);

  // Focus start recording button when component mounts
  useEffect(() => {
    startRecordingButtonRef.current?.focus();
  }, []);

  // Focus management when recording state changes
  useEffect(() => {
    if (isRecording && isPermissionGranted && !draftAlertVisibility) {
      // When recording starts, focus the pause button
      pauseResumeButtonRef.current?.focus();
    } else if (isRecordingComplete) {
      // When recording completes, focus the done button
      doneButtonRef.current?.focus();
    }
  }, [
    isRecording,
    isRecordingComplete,
    isPermissionGranted,
    draftAlertVisibility,
  ]);

  // Deactivate keyboard drag when focus leaves the drag handle
  useEffect(() => {
    if (!isKeyboardDragActive || !dragHandleRef.current) return;

    const handleFocusOut = e => {
      // Check if focus is moving away from the drag handle
      if (dragHandleRef.current?.contains(e.relatedTarget)) return;

      setIsKeyboardDragActive(() => {
        keyboardDragStartPositionRef.current = null;
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = "Dropped Audio Recorder";
        }
        return false;
      });
    };

    const dragHandle = dragHandleRef.current;
    dragHandle.addEventListener("focusout", handleFocusOut);

    return () => {
      dragHandle.removeEventListener("focusout", handleFocusOut);
    };
  }, [isKeyboardDragActive]);

  const handleKeyboardDrag = useCallback(
    e => {
      if (!isKeyboardDragActive) return;

      const STEP_SIZE = e.shiftKey ? DRAG_STEP_SIZE_WITH_SHIFT : DRAG_STEP_SIZE;
      let newX = position.x;
      let newY = position.y;
      let shouldDrop = false;
      let shouldCancel = false;
      let shouldMove = false;

      switch (e.key) {
        case "ArrowLeft":
          newX -= STEP_SIZE;
          shouldMove = true;
          break;
        case "ArrowRight":
          newX += STEP_SIZE;
          shouldMove = true;
          break;
        case "ArrowUp":
          newY -= STEP_SIZE;
          shouldMove = true;
          break;
        case "ArrowDown":
          newY += STEP_SIZE;
          shouldMove = true;
          break;
        case "Enter":
        case " ":
          shouldDrop = true;
          break;
        case "Escape":
          shouldCancel = true;
          break;
        default:
          return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Handle all state updates
      setPosition(prev => {
        if (shouldCancel) {
          return keyboardDragStartPositionRef.current || prev;
        }

        if (!shouldMove) {
          return prev;
        }

        const recorderWidthWithSafeSpace =
          audioRecorderRef.current.offsetWidth / 2 + SAFE_SPACE;
        const recorderHeightWithSafeSpace =
          audioRecorderRef.current.offsetHeight / 2 + SAFE_SPACE;

        const { clampedX, clampedY } = calculateClampedPosition(
          recorderHeightWithSafeSpace,
          recorderWidthWithSafeSpace,
          newX,
          newY
        );

        return { x: clampedX, y: clampedY };
      });

      setIsKeyboardDragActive(prev => {
        if (!shouldDrop && !shouldCancel) {
          return prev;
        }

        keyboardDragStartPositionRef.current = null;
        if (liveRegionRef.current) {
          const message = shouldDrop
            ? "Dropped Audio Recorder"
            : "Cancelled Dragging";
          liveRegionRef.current.textContent = message;
        }
        return false;
      });
    },
    [isKeyboardDragActive, position]
  );

  /**
   * Handles Escape key press to close the recorder or show discard confirmation.
   *
   * Behavior:
   * - Ignores if any modal is already open (discard/start-over) or during keyboard drag
   * - If no recording exists: closes the recorder immediately
   * - If recording in progress: pauses it first, then shows discard confirmation modal
   * - If recording is complete: shows discard confirmation modal
   */
  const handleEscapeKey = e => {
    // Early return if not Escape key or if modals are already open
    if (e.key !== "Escape") return;
    if (showDiscardModal || isStartOverDialogOpen || isKeyboardDragActive)
      return;

    e.preventDefault();
    e.stopPropagation();

    // If not recording at all, just close
    if (!isRecording && !isRecordingComplete) {
      closeModal();
      return;
    }

    // If recording, pause it first
    if (isRecording) {
      togglePause?.(true);
    }

    // Show discard modal for any recording state
    setShowDiscardModal(true);
  };

  const handleMouseDown = e => {
    const startX = e.clientX - position.x;
    const startY = e.clientY - position.y;
    document.body.style.userSelect = "none";
    const handleMouseMove = e => {
      e.stopPropagation();
      setInsideResumebutton(false);

      const newX = e.clientX - startX;
      const newY = e.clientY - startY;

      // divided the width by 2 because transform: translateX(-50%,-50%) is applied
      const recorderWidthWithSafeSpace =
        audioRecorderRef.current.offsetWidth / 2 + SAFE_SPACE;
      const recorderHeightWithSafeSpace =
        audioRecorderRef.current.offsetHeight / 2 + SAFE_SPACE;

      const { clampedX, clampedY } = calculateClampedPosition(
        recorderHeightWithSafeSpace,
        recorderWidthWithSafeSpace,
        newX,
        newY
      );
      setPosition({ x: clampedX, y: clampedY });
    };

    const handleMouseUp = () => {
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  useLayoutEffect(() => {
    if (hadDraftRecording) {
      setIsRecording(true);
    }

    if (audioRecorderRef.current) {
      const recorderHeightWithSafeSpace =
        audioRecorderRef.current.offsetHeight / 2 + SAFE_SPACE;
      const recorderWidthWithSafeSpace =
        audioRecorderRef.current.offsetWidth / 2 + SAFE_SPACE;

      let calculatedPosition = {
        x: window.innerWidth / 2,
        y: window.innerHeight - recorderHeightWithSafeSpace,
      };

      const hasValidInitialPosition =
        typeof initialPosition.x === "number" ||
        typeof initialPosition.y === "number";

      if (hasValidInitialPosition) {
        const { clampedX, clampedY } = calculateClampedPosition(
          recorderHeightWithSafeSpace,
          recorderWidthWithSafeSpace,
          initialPosition.x,
          initialPosition.y
        );

        calculatedPosition = {
          x: clampedX || calculatedPosition.x,
          y: clampedY || calculatedPosition.y,
        };
      }
      setPosition(calculatedPosition);
    }

    return () => {
      if (preRecordingTimer?.timeoutId) {
        clearTimeout(preRecordingTimer.timeoutId);
        clearInterval(preRecordingTimer.intervalId);
      }
      cancelRecording();
    };
  }, []);

  useImperativeHandle(forwardedRef, () => ({
    cancelRecording: () => {
      cancelRecording();
    },
  }));

  const onVisualiserContainerRefUpdate = useCallback(node => {
    if (node !== null) {
      node.style.height = "24px";
      handleVisualiserRefUpdate(node);
    }
  }, []);

  // Classes and styles
  const recordingDetailsContainerClasses = cx(
    classes.recordingDetails,
    "text-label"
  );

  const buttonSize =
    shouldBeResponsive && window.innerWidth < 470 && !draftAlertVisibility
      ? "small"
      : "medium";

  // attachment / pause state blob prepration
  const attachment = React.useMemo(() => {
    if (pauseStateBlob) {
      return prepareAttachmentFromBlob(pauseStateBlob);
    } else if (draftRecordingBlob) {
      return prepareAttachmentFromBlob(draftRecordingBlob);
    }
    return null;
  }, [draftRecordingBlob, pauseStateBlob]);

  // methods
  const onAlertClose = () => {
    cancelRecording();
    setAlertVisibility(false);
  };

  const cancelRecordingAndCloseModal = async () => {
    togglePause?.(true);
    await cancelRecording();
    closeModal();
  };

  const startCountdownAndRecording = () => {
    if (!isPermissionGranted) {
      setAlertVisibility(true);

      setTimeout(() => {
        if (audioRecorderRef.current) {
          const audioRecorderContainer = audioRecorderRef.current.getBoundingClientRect();
          const recorderHeightWithSafeSpace =
            audioRecorderRef.current.offsetHeight / 2 + SAFE_SPACE;
          // Check for bottom overflow
          if (
            Math.ceil(audioRecorderContainer.bottom) >=
            window.innerHeight - SAFE_SPACE
          ) {
            const calculatedPosition = {
              x: position.x,
              y: window.innerHeight - recorderHeightWithSafeSpace,
            };

            setPosition(calculatedPosition);
          }

          // Check for top overflow
          if (Math.floor(audioRecorderContainer.top) <= SAFE_SPACE) {
            const calculatedPosition = {
              x: position.x,
              y: recorderHeightWithSafeSpace,
            };

            setPosition(calculatedPosition);
          }
        }
      }, 0); // Delay recalculation to ensure height has updated
    } else {
      setIsPreRecordingState(true);
      const timerId = setTimeout(() => {
        setIsPreRecordingState(false);
        setIsRecording(true);
        setIsRecordingComplete(false);
        startRecording();
        clearInterval(preRecordingTimer.intervalId);
      }, 3000);
      const intervalId = setInterval(() => {
        setPreRecordingTimer(prevState => {
          if (prevState?.time < 0) {
            clearInterval(intervalId);
          }
          return {
            ...prevState,
            time: prevState.time - 1,
          };
        });
      }, 1000);
      setPreRecordingTimer({
        ...preRecordingTimer,
        timeoutId: timerId,
        intervalId: intervalId,
        showTimer: true,
      });
    }
  };

  const pauseRecordingAndShowOptions = () => {
    togglePause?.(true);
    setIsRecording(false);
    setIsRecordingComplete(true);
  };

  const stopRecordingAndStartOver = async () => {
    await cancelRecording();
    await restartRecording();
  };

  const audioRecorderContainerClasses = cx(
    classes.audioRecorderContainer,
    "dark",
    {
      [classes.audioRecorderWithAlert]:
        (alertVisiblity && !isPermissionGranted) || draftAlertVisibility,
      [classes.audioContainerShrinkingAnimation]: insideResumebutton,
      [classes.audioRecorderResponsive]: shouldBeResponsive,
    }
  );

  const visualiserContainerClasses = cx(classes.visualiserContainer, {
    [classes.visualiserContainerResponsive]: shouldBeResponsive,
  });

  const hideRecorderContainer = isPaused || isRecordingComplete;

  useEffect(() => {
    if (!activeAudioRecorder)
      setAudioRecorderActive({
        ...activeAudioRecorder,
        recorderAlreadyActive: true,
        closeActiveAudioRecorder: cancelRecordingAndCloseModal,
      });
    return () => {
      setAudioRecorderActive(null);
    };
  }, []);

  const resumeDraftAndReorderRecorder = async () => {
    const initialBottom = audioRecorderRef.current.getBoundingClientRect()
      .bottom;
    if (draftAlertVisibility) {
      await setDraftAlertVisibility(false);
      const halfRecorderHeight =
        audioRecorderRef.current.getBoundingClientRect().height / 2;
      setPosition({
        x: position.x,
        y: initialBottom - halfRecorderHeight,
      });
    }
    togglePause?.(!isPaused);
  };

  const renderStrip = () => {
    return (
      <>
        <div
          aria-roledescription="draggable"
          aria-label="Audio Recorder"
          className={audioRecorderContainerClasses}
          style={{
            position: "absolute",
            left: `${position.x}px`,
            top: `${position.y}px`,
          }}
          ref={audioRecorderRef}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            handleKeyboardDrag(e);
            handleEscapeKey(e);
          }}
          tabIndex={-1}
        >
          {draftAlertVisibility && (
            <Alert
              message={t("common:draft_recording_alert")}
              type={"warning"}
              dsVersion="2.0"
              variant="inline"
              actionElementPosition="bottom"
              actionElement={
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button
                    size="medium"
                    icon={<TickCircleFilled />}
                    onClick={stopRecording}
                    dsVersion="2.0"
                    style={{ color: "#fafafa" }}
                    testId={`${testId}-draftAlert-submitRecording`}
                  >
                    {submitText ?? t("common:add_recording")}
                  </Button>
                  <Button
                    size="medium"
                    variant="neutral"
                    type="outlined"
                    icon={<ReloadArrowOutlined />}
                    onClick={() => {
                      togglePause?.(true);
                      setIsStartOverDialogOpen(true);
                    }}
                    dsVersion="2.0"
                    style={{ color: "#fafafa" }}
                    testId={`${testId}-draftAlert-restartRecording`}
                  >
                    {_.capitalize(t("common:start_over"))}
                  </Button>
                </div>
              }
              testId={`${testId}-draftRecording`}
            />
          )}
          <div className={classes.recorderInnerContainer}>
            <Tooltip tooltip={t("common:drag")} destroyTooltipOnScroll={false}>
              <IconButton
                ref={dragHandleRef}
                aria-label={t("common:drag")}
                aria-describedby="AudioRecorderDragDescription"
                aria-pressed={isKeyboardDragActive}
                icon={<DotsSixVerticalOutlined />}
                dsVersion="2.0"
                variant="neutral"
                type="plain"
                size="small"
                onMouseDown={e => {
                  e.preventDefault();
                  handleMouseDown(e);
                }}
                onKeyDown={e => {
                  // Only activate keyboard drag via Enter or Space
                  if (e.key !== "Enter" && e.key !== " ") return;

                  e.preventDefault();
                  e.stopPropagation();
                  setIsKeyboardDragActive(prev => {
                    const isActivating = !prev;

                    // Save or clear starting position
                    keyboardDragStartPositionRef.current = isActivating
                      ? position
                      : null;

                    // Announce drag state to screen readers
                    if (liveRegionRef.current) {
                      const announcement = isActivating
                        ? "Picked Up Audio Recorder"
                        : "Dropped Audio Recorder";
                      liveRegionRef.current.textContent = announcement;
                    }

                    return isActivating;
                  });
                }}
                testId={`${testId}-dragHandle`}
              />
            </Tooltip>
            <div className={classes.visualsContainer}>
              <div
                className={cx(classes.recordingActionsContainer, {
                  [classes.recordingActionsContainerHidden]: hideRecorderContainer,
                })}
                style={{ width: "100%" }}
              >
                <div
                  className={visualiserContainerClasses}
                  ref={onVisualiserContainerRefUpdate}
                ></div>
                <div
                  className={cx(classes.recordingIcon, {
                    [classes.active]: isRecording && !isPaused,
                  })}
                />
                {(isRecording || !isRecordingComplete) && !isPaused && (
                  <div className={recordingDetailsContainerClasses}>
                    <span>{timer}</span>
                    {showLimit ? <span>/{formattedLimit}</span> : null}
                  </div>
                )}
              </div>
              {hideRecorderContainer && (
                <MediaPlaceholder
                  attachments={[attachment]}
                  containerStyle={{
                    background: "none",
                    border: "none",
                    width: "100%",
                    bottom: 0,
                    left: 0,
                    position: "absolute",
                    right: 0,
                    top: 0,
                  }}
                  showAudioDownloadButton={showAudioDownloadButton}
                  showWaveform={true}
                  showAudioWaveformPlayerV2={true}
                  pausedRecordingState={isPaused && !isRecordingComplete}
                  testId={testId}
                  shouldBeResponsive={
                    shouldBeResponsive && !draftAlertVisibility
                  }
                />
              )}
            </div>
            <div className={classes.actionsContainer}>
              {/* If recording has begun and permissions have been granted
          then show the pause/resume and sto buttons
          else show start button or post recording buttons based on isRecordingComplete */}
              {isRecording && isPermissionGranted ? (
                <>
                  {
                    <Button
                      ref={pauseResumeButtonRef}
                      icon={isPaused ? <MicrophoneOutlined /> : <PauseFilled />}
                      size={buttonSize}
                      dsVersion="2.0"
                      variant="neutral"
                      type="outlined"
                      onClick={resumeDraftAndReorderRecorder}
                      onMouseEnter={() => setInsideResumebutton(true)}
                      onMouseLeave={() =>
                        draftAlertVisibility && setInsideResumebutton(false)
                      }
                      testId={`${testId}-toggleResumePause`}
                    >
                      {isPaused ? t("common:resume") : t("common:pause")}
                    </Button>
                  }
                  {!draftAlertVisibility && (
                    <>
                      <Tooltip
                        tooltip={t("common:restart")}
                        destroyTooltipOnScroll={false}
                      >
                        <IconButton
                          aria-label={t("common:restart")}
                          icon={<ReloadArrowOutlined />}
                          size={buttonSize}
                          dsVersion="2.0"
                          variant="neutral"
                          type="outlined"
                          onClick={() => {
                            togglePause?.(true);
                            setIsStartOverDialogOpen(true);
                          }}
                          testId={`${testId}-restartRecording`}
                        />
                      </Tooltip>
                      <Button
                        size={buttonSize}
                        icon={
                          draftAlertVisibility ? (
                            <TickCircleFilled />
                          ) : (
                            <SquareFilled />
                          )
                        }
                        onClick={
                          draftAlertVisibility
                            ? stopRecording
                            : pauseRecordingAndShowOptions
                        }
                        dsVersion="2.0"
                        style={{ color: "#fafafa" }}
                        testId={`${testId}-stopRecording`}
                      >
                        {draftAlertVisibility
                          ? submitText ?? t("common:done")
                          : t("common:stop")}
                      </Button>
                    </>
                  )}
                </>
              ) : isRecordingComplete ? (
                <>
                  <Tooltip
                    tooltip={t("common:restart")}
                    destroyTooltipOnScroll={false}
                  >
                    <IconButton
                      aria-label={t("common:restart")}
                      icon={<ReloadArrowOutlined />}
                      size={buttonSize}
                      dsVersion="2.0"
                      variant="neutral"
                      type="outlined"
                      onClick={() => setIsStartOverDialogOpen(true)}
                      testId={`${testId}-restartRecording`}
                    />
                  </Tooltip>
                  <Button
                    ref={doneButtonRef}
                    size={buttonSize}
                    icon={<TickCircleFilled />}
                    onClick={stopRecording}
                    dsVersion="2.0"
                    style={{ color: "#fafafa" }}
                    testId={`${testId}-submitRecording`}
                  >
                    {submitText ?? t("common:done")}
                  </Button>
                </>
              ) : isPreRecordingState ? (
                <Button
                  isActivated={!preRecordingTimer.showTimer}
                  size={buttonSize}
                  style={{ color: " #FAFAFA" }}
                  dsVersion="2.0"
                >
                  {`${t("common:recording_begins_in")} ${
                    preRecordingTimer.time
                  }..`}
                </Button>
              ) : (
                <Button
                  ref={startRecordingButtonRef}
                  size={buttonSize}
                  icon={<ScreenRecordOutlined />}
                  onClick={startCountdownAndRecording}
                  dsVersion="2.0"
                  style={{ color: " #FAFAFA" }}
                  testId={`${testId}-startRecording`}
                >
                  {t("common:start_recording")}
                </Button>
              )}
              <Tooltip
                tooltip={t("common:close")}
                destroyTooltipOnScroll={false}
              >
                <IconButton
                  aria-label={t("common:close")}
                  icon={<CloseOutlined />}
                  variant="neutral"
                  type="plain"
                  size={buttonSize}
                  onClick={() => {
                    if (isRecording || isRecordingComplete) {
                      isRecording && togglePause?.(true);
                      setShowDiscardModal(true);
                    } else closeModal();
                  }}
                  dsVersion="2.0"
                  testId={`${testId}-closeRecorder`}
                />
              </Tooltip>
            </div>
          </div>
          {!isPermissionGranted && alertVisiblity && (
            <Alert
              message={t("common:microphone_permission_message")}
              type={"warning"}
              isClosable={true}
              onClose={onAlertClose}
              variant={"inline"}
              dsVersion="2.0"
              testId={`${testId}-micPermission`}
            />
          )}
        </div>
        <div
          ref={liveRegionRef}
          role="status"
          aria-live="assertive"
          aria-atomic="true"
          className={classes.audioRecorderDragLiveRegion}
        />
        <div id="AudioRecorderDragDescription" style={{ display: "none" }}>
          To pick up a draggable item, press the space bar or enter. While
          dragging, use the arrow keys to move the item. Press space bar or
          enter to drop the item in its new position, or press escape to cancel
          and return to the original position.
        </div>
      </>
    );
  };
  return (
    <>
      {isStartOverDialogOpen && (
        <Dialog
          buttonOneText={t("common:keep")}
          buttonTwoText={t("common:discard_and_start_over")}
          message={t("common:restart_recording_text")}
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEsc={true}
          title={t("common:restart_recording")}
          onButtonOneClick={() => setIsStartOverDialogOpen(false)}
          onButtonTwoClick={stopRecordingAndStartOver}
          onClose={() => setIsStartOverDialogOpen(false)}
          testId={testId}
          dsVersion="2.0"
        />
      )}
      {showDiscardModal &&
        (isRecordingComplete ? (
          <Dialog
            buttonOneText={t("common:keep")}
            buttonTwoText={t("common:discard_and_close_text")}
            message={t("common:discard_recording_text")}
            shouldCloseOnEsc={true}
            shouldCloseOnOverlayClick={false}
            title={t("common:discard_recording")}
            onButtonOneClick={() => setShowDiscardModal(false)}
            onButtonTwoClick={cancelRecordingAndCloseModal}
            onClose={() => setShowDiscardModal(false)}
            dsVersion="2.0"
            testId={testId}
          />
        ) : (
          <Dialog
            buttonOneText={t("common:continue_recording")}
            buttonTwoText={t("common:exit_and_close")}
            message={t("common:exit_recording_text")}
            shouldCloseOnEsc={true}
            shouldCloseOnOverlayClick={false}
            title={t("common:exit_recording")}
            onButtonOneClick={() => {
              isRecording && togglePause?.(true);
              setShowDiscardModal(false);
            }}
            onButtonTwoClick={cancelRecordingAndCloseModal}
            onClose={() => setShowDiscardModal(false)}
            dsVersion="2.0"
            testId={testId}
          />
        ))}
      {/*
        ! Using react portal here instead of UIModal or DS-Modal because
          These two modals are obstructing the audio recording "post-draft state"
          i.e. if you have something drafted and you open the modal, the modal
          gives you two options to either discard the draft or continue recording
          but the modal is obstructing the normal flow of these two options

          1. If you continue recording the previous draft is discarded and wont't
            be merged with the new recording.

          2. If you discard recording, the draft is discarded but the recorder doesn't
            pauses the timer while the recording is actually paused. So the actual recording
            is smaller than the timer.

          Using react portal solved these issues and the features are working as expected.
       */}
      {createPortal(renderStrip(), document.body)}
    </>
  );
});

const mapActionCreators = {
  setToastMsg,
  updateAudioRecorderBlob,
  setAudioRecorderActive,
  downloadFromUrl,
};
const mapStateToProps = state => {
  return {
    draftRecording: _.get(state, "app_services.draftRecording", null),
    activeAudioRecorder: _.get(
      state,
      "app_services.activeAudioRecorder",
      false
    ),
  };
};

const ComposedAudioRecorderV5 = compose(
  I18nHOC({ resource: ["common", "workbook"] }),
  connect(mapStateToProps, mapActionCreators, null, { forwardRef: true }),
  withRecorder
)(AudioRecorderV5);

const defaultProps = {
  autoStartRecording: false,
  limit: 3540000,
  saveDraftRecording: true,
  showAudioDownloadButton: true,
  shouldBeResponsive: false,
};

const propTypes = {
  showAudioDownloadButton: PropTypes.bool,
  shouldBeResponsive: PropTypes.bool,
};

AudioRecorderV5.displayName = "AudioRecorderV5";
AudioRecorderV5.defaultProps = defaultProps;
AudioRecorderV5.propTypes = propTypes;

ComposedAudioRecorderV5.defaultProps = defaultProps;

export { ComposedAudioRecorderV5 as AudioRecorderV5 };
